import { useState } from "react";
import { Button, Form, Spin } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { Col, Row } from "reactstrap";
import FormField from "../../../../common/Form/FormField";
import SelectField from "../../../../common/Form/SelectField";
import { addReversement, askVersementBank } from "../../services/versementApi";
import { getBusinessByMarchandId, getPaymentMethodeBusiness } from "../../../developer/services/developerApi";
import "../../utils/style.css";
import VersmentConfirm from "./versementConfirm";
import CountrySelect from "../../../../common/Form/CountrySelect";
import { FormatErrorVersement } from "../../../../common/errorformat/errorFormat";
import { useSecurityFlow } from "../../../../common/secure/useSecurityFlow";

const AddReversement = ({
  accounts,
  isLoadAccount,
  isErrorAcc,
  errorAcc,
  onSuccessReversement,
  accountsBanks,
  isLoadBanks,
  isErrorBanks,
}) => {
  const [form] = Form.useForm();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [displayData, setDisplayData] = useState(null);
  const [errorV, setErrorV] = useState("");

  const queryClient = useQueryClient();
  const currentUser = JSON.parse(sessionStorage.getItem("authUser"));
  const merchantId = currentUser?.user?.id || currentUser?.user?.merchantId;
  const twoFaEnabled = currentUser?.twoFaEnabled;
  const token = currentUser?.token;
  const role = currentUser?.roles;
  const permissions = currentUser?.user?.permissions;
  const isAuthorized = role === 'COLLABORATOR' ? permissions?.includes('REQUEST_PAYOUT') : true;

  const { runSecureAction, modals } = useSecurityFlow({twoFaEnabled});

  const { data: businessList, isLoading, isError, error } = useQuery(
    ["business", merchantId],
    () => getBusinessByMarchandId(merchantId),
    { enabled: !!merchantId }
  );

  const { data: pmb, isLoading: isLoadingPMB } = useQuery(
    ["pmb", selectedBusiness?.id],
    () => getPaymentMethodeBusiness(selectedBusiness?.id),
    { enabled: !!selectedBusiness?.id }
  );

  const validationSchema = Yup.object().shape({
    businessId: Yup.string().required("Veuillez choisir votre business"),
    country: Yup.object().nullable().required("Veuillez choisir un pays"),
    amount: Yup.number()
      .required("Veuillez saisir le montant demandé")
      .when("verseMode", {
        is: "bank",
        then: (schema) => schema.min(50000, "Le montant minimum pour un virement bancaire est de 50 000"),
        otherwise: (schema) => schema.min(1, "Le montant minimum est de 100"),
      }),
    receptionAccount: Yup.string().required("Veuillez choisir un compte de réception"),
  });

  const initialValues = {
    businessId: "",
    country: null,
    amount: "",
    receptionAccount: "",
    paymentMethod: "",
    phone: "",
    prefix: "",
    ownerFullName: "",
    availableBalance: 0,
    verseMode: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: () => {
      const data = dataConfirm();
      if (!data) return;
      setDisplayData(data);
      setIsConfirmModalVisible(true);
    },
  });

  const handleFinalSubmit = async (values) => {
    if (!values) return;
    setIsLoadingSubmit(true);

    const amount = Number(values.amount) || 0;
    const frais = values.verseMode === "mobile" ? amount * 0.025 : 0;
    const totalAmount = amount + frais;
    const data = {
      token,
      merchantId: merchantId,
      amount: totalAmount,
      currency: values.country?.currency,
      paymentMethod: values.paymentMethod,
      phoneNumber: values.phone,
      prefixPhone: values.prefix?.replace("+", ""),
      name: values.ownerFullName,
      idClient: "1234565",
      email: currentUser?.email,
      country: values.country?.country,
      ipn: "https://api.systalink.com/api/v1/payment/callback",
    };

    const parsedAccount = JSON.parse(values.receptionAccount);
    const ask = {
      token,
      amount: values.amount,
      currency: values.country?.currency,
      country: values.country?.country,
      accountId: parsedAccount?.id,
      merchantId: merchantId,
    };

    try {
      if (values.verseMode === "mobile") {
        await addReversement(data, selectedBusiness);
        toast.success("Votre reversement a été effectué avec succès");
      } else if (values.verseMode === "bank") {
        await askVersementBank(ask, selectedBusiness);
        toast.success("Votre demande de versement a été initialisé");
      }

      setErrorV(null);
      formik.resetForm();
      setSelectedBusiness(null);
      setDisplayData(null);
      await queryClient.invalidateQueries("authUser");
      if (onSuccessReversement) onSuccessReversement();
    } catch (error) {
      const formatted = FormatErrorVersement(error?.response?.data);
      setErrorV(formatted.message);
      toast.error(formatted.message);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const handleBusinessChange = (val) => {
    const selected = businessList?.find((b) => String(b.id) === String(val));
    if (selected) setSelectedBusiness(selected);
  };

  const handleChange = (e) => {
    formik.handleChange(e);
    const selected = JSON.parse(e.target.value);
    formik.setFieldValue("prefix", selected.prefixCode);
    formik.setFieldValue("phone", selected.phoneNumber);
    formik.setFieldValue("paymentMethod", selected.paymentMethod);
    formik.setFieldValue("ownerFullName", selected.ownerFullName);
    formik.setFieldValue("receptionAccount", e.target.value);
  };

  const availableBalance = formik.values.availableBalance || 0;

  const verseList = [
    { id: "mobile", label: "Mobile Money" },
    { id: "bank", label: "Virement bancaire" },
  ];

  const verseMode = formik.values.verseMode;

  const handleVerseModeChange = (value) => {
    formik.setFieldValue("verseMode", value);
    formik.setFieldValue("receptionAccount", "");
  };

  const { businessId, country, receptionAccount } = formik.values;

  const dataConfirm = () => {
    if (!formik.values.businessId || !formik.values.receptionAccount) return null;

    const business = businessList?.find(b => Number(b.id) === Number(formik.values.businessId));
    const accountData =
      verseMode === "bank"
        ? accountsBanks?.find(a => JSON.stringify(a) === formik.values.receptionAccount)
        : accounts?.find(a => JSON.stringify(a) === formik.values.receptionAccount);

    let accountName = "";
    let accountDetails = "";

    if (verseMode === "bank") {
      accountName = accountData?.maskedIban || "-";
      accountDetails = accountData?.ownerFullName || "-";
    } else {
      accountName = accountData?.paymentMethod || "-";
      accountDetails = `${accountData?.prefixCode || ""} ${accountData?.phoneNumber || ""}`;
    }

    const amount = Number(formik.values.amount) || 0;
    const frais = verseMode === "mobile" ? amount * 0.025 : 0;

    return {
      ...formik.values,
      businessName: business?.businessName,
      accountName,
      accountDetails,
      amount,
      frais
    };
  };

  const amount = Number(formik.values.amount) || 0;
  const frais = verseMode === "mobile" ? amount * 0.025 : 0;
  const totalAmount = amount + frais;

  return (
    <div className="add-rever-card position-relative">
      {isLoadingSubmit && (
        <div className="reversement-overlay-spin">
          Envoie en cours ... <Spin size="large" />
        </div>
      )}
      <div className="text-center text-muted mt-2 mb-4">
        Merci de renseigner et vérifier les informations avant de confirmer votre versement.
      </div>
      <Form form={form} layout="vertical">
        <Row>
          {errorV && <div className="alert alert-danger">{errorV}</div>}
          <SelectField
            label="Business"
            name="businessId"
            formik={formik}
            colSize={6}
            nameSelect="votre business"
            options={businessList}
            onChange={handleBusinessChange}
            isLoading={isLoading}
            isError={isError}
            error={error}
          />

          <Col lg={6}>
            <div className="mb-3">
              <label className="form-label  label">Pays</label>
              <CountrySelect
                options={pmb}
                value={formik.values.country || null}
                onChange={(selected) => {
                  formik.setFieldValue("country", selected);
                  formik.setFieldValue("availableBalance", selected?.availableBalance);
                }}
                disabled={!businessId}
              />
              {formik.touched.country && formik.errors.country && (
                <div className="text-danger">{formik.errors.country}</div>
              )}
            </div>
          </Col>

          <SelectField
            label="Mode de versement"
            name="verseMode"
            nameSelect="le mode de versement"
            previousNameSelect="le pays"
            formik={formik}
            options={verseList}
            error={formik.errors.type}
            colSize={12}
            onChange={handleVerseModeChange}
            disabled={!country || availableBalance <= 0}
          />

          {verseMode === "mobile" ? (
            <Col lg={6}>
              <div className="mb-3">
                <label className="form-label">Compte Mobile Money</label>
                <select
                  name="receptionAccount"
                  className="form-control"
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.receptionAccount}
                  disabled={availableBalance <= 0}
                >
                  <option value="">Choisir votre compte</option>
                  {isLoadAccount ? (
                    <option>Chargement...</option>
                  ) : isErrorAcc ? (
                    <option>Erreur, actualisez ou contactez le support</option>
                  ) : (
                    accounts?.map((a) => (
                      <option key={a.id} value={JSON.stringify(a)}>
                        {a.paymentMethod} ({a.prefixCode} {a.phoneNumber})
                      </option>
                    ))
                  )}
                </select>
              </div>
            </Col>
          ) : verseMode === "bank" ? (
            <Col lg={6}>
              <div className="mb-3">
                <label className="form-label">Compte bancaire</label>
                <select
                  name="receptionAccount"
                  className="form-control"
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.receptionAccount}
                  disabled={availableBalance <= 0}
                >
                  <option value="">Choisir votre compte</option>
                  {isLoadBanks ? (
                    <option>Chargement...</option>
                  ) : isErrorBanks ? (
                    <option>Erreur, actualisez ou contactez le support</option>
                  ) : (
                    accountsBanks?.map((a) => (
                      <option key={a.id} value={JSON.stringify(a)}>
                        {a.maskedName || ""} - ({a.maskedIban || ""}) - {a.nameBank || ""}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </Col>
          ) : (
            <Col lg={12}>
              <div className="select-versement-message"> Merci de choisir le mode de versement</div>
            </Col>
          )}

          {verseMode && (
            <>
              <FormField
                label="Montant demandé (+Frais)"
                name="amount"
                formik={formik}
                colSize={6}
                placeholder="Montant minimum 100"
                type="number"
                disabled={!receptionAccount || availableBalance <= 0}
              />
              { amount > 0 && (
                <Col lg={12} md={12}>
                  <div className="frais-versement text-end">
                    Total débité :{" "}
                    <span>{totalAmount.toLocaleString()} {formik.values.country?.currency || ""}</span>
                  </div>
                </Col>
              )} 
            </>
          )}
          
        </Row>

        <Button
          type="primary"
          className="mt-3 btn-confirm-reversement"
          onClick={formik.handleSubmit}
          disabled={!isAuthorized || !formik.isValid || !formik.dirty || isLoadingSubmit || currentUser?.adminImpersonation}
        >
          {isAuthorized && !currentUser?.adminImpersonation ? "Confirmer" : "Pas autorisé à effectuer un versement."}
        </Button>
      </Form>

      <VersmentConfirm
        visible={isConfirmModalVisible}
        onClose={() => setIsConfirmModalVisible(false)}
        data={displayData}
        onConfirm={() => {
          setIsConfirmModalVisible(false);
          runSecureAction(() => handleFinalSubmit(formik.values));
        }}
      />

      {modals}
    </div>
  );
};

export default AddReversement;